import { format, formatDistanceToNow, parseISO } from 'date-fns';

function toDate(value: string | Date): Date {
  return typeof value === 'string' ? parseISO(value) : value;
}

/**
 * Today's IST calendar date as `yyyy-MM-dd`.
 *
 * The server decides what "today" is in IST (`istDateKey` in tasks.recurrence.ts), so a
 * client using its own local date would disagree with it — a machine set to UTC is on the
 * previous day between 18:30 and midnight UTC, which is enough to block someone from
 * picking the date the server considers today.
 *
 * `en-CA` is not cosmetic: it is the locale that formats as `yyyy-MM-dd`, which is both
 * what the API expects and what sorts correctly as a string.
 */
export function getTodayIST(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

/**
 * First and last calendar day of a month as `yyyy-MM-dd` (`month` is 1–12).
 *
 * Built from the date parts, never `toISOString()`: local midnight on the last day
 * of the month is still the previous day in UTC for anyone east of Greenwich, which
 * silently cut that last day off the month filter on the approvals pages in IST.
 */
export function monthDateRange(year: number, month: number): { from: string; to: string } {
  const mm = String(month).padStart(2, '0');
  const lastDay = String(new Date(year, month, 0).getDate()).padStart(2, '0');
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${lastDay}` };
}

/** "06 Apr 2026" */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return format(toDate(value), 'dd MMM yyyy');
}

/** "06/04/2026" */
export function formatDateDMY(value: string | Date | null | undefined): string {
  if (!value) return 'N/A';
  return format(toDate(value), 'dd/MM/yyyy');
}

/** "06 Apr 2026, 14:30" */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return format(toDate(value), 'dd MMM yyyy, HH:mm');
}

const IST_DATE_TIME = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const IST_TIME = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * "06 Apr 2026, 02:30 pm" in IST whatever the browser's zone — for instants that come from
 * another server (tracker, Rate API), where `formatDateTime`'s local time would drift.
 */
export function formatDateTimeIST(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDate(value);
  return Number.isNaN(date.getTime()) ? '—' : IST_DATE_TIME.format(date);
}

/** "02:30 pm" in IST. */
export function formatTimeIST(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDate(value);
  return Number.isNaN(date.getTime()) ? '—' : IST_TIME.format(date);
}

/** "3 hours ago" */
export function formatRelativeTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  return formatDistanceToNow(toDate(value), { addSuffix: true });
}
